import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../../services/SupabaseClient";
import { getMessagesOfChatRoom } from "../../services/ChatService";

import Message from "./Message";
import Contact from "./Contact";
import ChatForm from "./ChatForm";

export default function ChatRoom({ currentChat, currentUser, socket }) {
  const [messages, setMessages] = useState([]);
  const [incomingMessage, setIncomingMessage] = useState(null);

  const scrollRef = useRef();

  // Função para buscar mensagens - usa customerId
  const fetchMessages = useCallback(async () => {
    if (!currentChat?.customerId) return;

    const { data, error } = await supabase.rpc("get_messages_by_customer_id", {
      customer_id_input: currentChat.customerId
    });

    if (error) {
      console.error("Erro ao buscar mensagens:", error);
      return;
    }

    const formatted = data.map((msg) => ({
      sender: msg.sender === "patient" ? currentChat.customerId : "sofia",
      message: msg.message,
      timestamp: new Date(Number(msg.ts) * 1000)
    }));

    setMessages(formatted);
  }, [currentChat?.customerId]);

  // 1. Carrega mensagens iniciais
  useEffect(() => {
    if (currentChat?.customerId) {
      fetchMessages();
    }
  }, [currentChat?.customerId, fetchMessages]);

  // 2. Supabase Realtime (escuta novas mensagens do banco)
  useEffect(() => {
    if (!currentChat?.id || !currentChat?.customerId) return;

    const channel = supabase
      .channel(`chat-room-${currentChat.id}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sessions',
          filter: `session_id=eq.${currentChat.id}`
        },
        (payload) => {
          console.log("[Realtime] Mudança detectada por session_id:", payload);
          fetchMessages();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sessions',
          filter: `customer_id=eq.${currentChat.customerId}`
        },
        (payload) => {
          console.log("[Realtime] Mudança detectada por customer_id:", payload);
          fetchMessages();
        }
      )
      .subscribe((status) => {
        console.log("[Realtime] Status da subscription:", status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentChat?.id, currentChat?.customerId, fetchMessages]);

  // 3. Scroll automático
  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages]);

  // 4. Recebe mensagens via socket
  useEffect(() => {
    socket.current?.on("getMessage", (data) => {
      setIncomingMessage({
        sender: data.sender,
        message: data.message,
      });
    });
  }, [socket]);

  useEffect(() => {
    if (incomingMessage) {
      setMessages((prev) => [...prev, incomingMessage]);
    }
  }, [incomingMessage]);

  // 5. Envia mensagem (via Supabase RPC)
  const handleFormSubmit = async (messageText) => {
    const receiverId = currentChat.members.find(
      (member) => member !== currentUser.uid
    );

    // Envia via socket (apenas para outros clientes)
    socket.current.emit("sendMessage", {
      sender: currentUser.uid,
      receiverId,
      message: messageText,
    });

    // Salva no Supabase — isso será captado pelo Realtime
    await supabase.rpc("add_message_to_session", {
      session: currentChat.id,
      role: "scheduler", // ou "sofia", conforme sua lógica
      text: messageText
    });
  };

  return (
    <div className="lg:col-span-2 lg:block">
      <div className="w-full">
        <div className="p-3 bg-white border-b border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <Contact chatRoom={currentChat} currentUser={currentUser} showPhone={true} />
        </div>

        <div className="relative w-full p-6 overflow-y-auto h-[30rem] bg-white border-b border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <ul className="space-y-2">
            {messages.map((message, index) => (
              <div key={index} ref={scrollRef}>
                <Message message={message} self={currentUser.uid} />
              </div>
            ))}
          </ul>
        </div>

        <ChatForm handleFormSubmit={handleFormSubmit} />
      </div>
    </div>
  );
}